import { useMemo } from 'react';
import { LineBreak } from '@direct-frontend/components';
import { useYandexKit } from '#shared/components/YandexKitProvider';
import { useConstructorContext } from '../components/ConstructorProvider';
import { CONSTRUCTOR_MYSTIQUE_ID } from '../model/constants';
import { getConstructorSectionCount, useConstructorPageSettings, useConstructorSectionSettings } from '../model/slices';
import { useConstructorCanAddSections } from './useConstructorCanAddSections';
import { useDenormalizedConstructorSection } from './useDenormalizedConstructorSection';

export const useAllowedToPasteAddSection = (props: { pageId: string; sectionInstanceId: string }) => {
    const { pageId, sectionInstanceId } = props;

    const section = useConstructorSectionSettings(pageId, sectionInstanceId);
    const page = useConstructorPageSettings(pageId);
    const canAddSections = useConstructorCanAddSections(page?.alias);
    const yandexKit = useYandexKit();
    const denormalizeSection = useDenormalizedConstructorSection();

    const { copiedSection } = useConstructorContext();

    const sectionSettings = useMemo(() => {
        if (section.widget !== CONSTRUCTOR_MYSTIQUE_ID) {
            return yandexKit.getSettings(section.widget);
        }

        const denormalizedSection = denormalizeSection(section);

        if (!denormalizedSection) {
            return null;
        }

        if ('template' in denormalizedSection && denormalizedSection.template.alias) {
            return yandexKit.getSettings(denormalizedSection.template.alias);
        }

        return yandexKit.getSettings(denormalizedSection);
    }, [yandexKit, section, denormalizeSection]);

    const allowedToAdd = useMemo(() => {
        if (!copiedSection) {
            return {
                allowed: false,
                message: null,
                pasteNext: false,
            };
        }

        const copiedSectionSettings = yandexKit.getSettings(
            denormalizeSection({
                ...copiedSection.sectionSettings,
                instance_id: copiedSection.sectionInstanceId,
            })
        );
        const { count } = getConstructorSectionCount(copiedSection.widget);

        const pasteNext = copiedSectionSettings?.displaySequence === undefined && !copiedSection.moduleId;
        const pageAllowedCheck =
            copiedSectionSettings?.supportedPages && Array.isArray(copiedSectionSettings.supportedPages)
                ? copiedSectionSettings.supportedPages.some((supportedAlias) => supportedAlias === page?.alias)
                : true;

        const sectionAdditionCheck = Boolean(copiedSection.moduleId) || canAddSections;

        const maxCountCheck = copiedSectionSettings?.maximum ? count < copiedSectionSettings.maximum : true;

        const moduleCheck = !copiedSection.moduleId || (copiedSection && copiedSection.widget === section.widget);

        const sectionCheck = sectionSettings?.displaySequence === undefined;

        const moduleTypeCheck = !copiedSection.moduleId || !sectionSettings?.modulesCopyWhitelist;

        let message = null;

        if (!moduleCheck) {
            message = (
                <>
                    Элемент можно вставить только <LineBreak mode="compact" />в секцию{' '}
                    {copiedSectionSettings?.title || 'того же типа, из которого она была скопирована'}
                </>
            );
        } else if (!maxCountCheck) {
            message = (
                <>
                    Секция уже есть на странице. <LineBreak mode="compact" />
                    Вы можете заменить текущую.
                </>
            );
        }

        return {
            allowed:
                sectionAdditionCheck &&
                pageAllowedCheck &&
                maxCountCheck &&
                moduleCheck &&
                moduleTypeCheck &&
                sectionCheck,
            message,
            pasteNext,
        };
    }, [canAddSections, page, copiedSection, yandexKit, section.widget, sectionSettings, denormalizeSection]);

    return allowedToAdd;
};
